import React from "react";
import styled from "styled-components";
import moment from "moment";

import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import { Divider } from "js/Components/Divider";

import { calculatePriceWithPromo, formatPrice } from "../../utils/prices";
import { PromoForm, PromoCoupon } from "../../components/PromoForm/PromoForm";
import type { PlanSummaryProps } from "./PlanSummary";
import { t } from "js/i18n";

const SummaryCard = styled.div`
    background: #f7fafc;
    border: 1px solid #dae9f0;
    border-radius: 2px;
    padding: 20px;
    width: 100%;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SummaryTitle = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 10px;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #444444;
    min-width: 100%;
    width: min-content;
`;

const SummaryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
`;

const SummaryToggle = styled.button`
    background: none;
    border: none;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-weight: 600;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const DueTodayText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: 0.5px;
    color: #444444;
`;

const DueDateText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.25;
    color: #888888;
`;

const DownArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <g clipPath="url(#clip0_68_229)">
            <path d="M4.63125 5.36865L7.5 8.23115L10.3688 5.36865L11.25 6.2499L7.5 9.9999L3.75 6.2499L4.63125 5.36865Z" fill="#11A9E2" />
        </g>
        <defs>
            <clipPath id="clip0_68_229">
                <rect width="15" height="15" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const UpArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <path d="M10.3688 9.63135L7.5 6.76885L4.63125 9.63135L3.75 8.7501L7.5 5.0001L11.25 8.7501L10.3688 9.63135Z" fill="#11A9E2" />
    </svg>
);

export function PlanSummary({ isTrial, billingInterval, productDisplayName, initialPromotionCode, prices, promo, availableReferralCredit, taxAmount, handleVerifyPromoCode, expandable, defaultExpanded }: PlanSummaryProps & { expandable?: boolean, defaultExpanded?: boolean }) {
    const [isSummaryExpanded, setIsSummaryExpanded] = React.useState(defaultExpanded ?? false);
    const isAnnual = billingInterval === "year";

    const currentPrice = prices[billingInterval].amount / 100;
    const currentPromoAppliedPrice = calculatePriceWithPromo(currentPrice, promo);

    const annualCostAtMonthlyRate = prices.month.amount * 12 / 100;
    const annualCostAtAnnualRate = prices.year.amount / 100;

    const dueToday = isTrial ? 0 : Math.max(currentPromoAppliedPrice - (availableReferralCredit / 100), 0);
    const dueNextBillingCycle = currentPromoAppliedPrice;

    const trialExpirationDate = moment().add(14, "days").format("MMMM Do, YYYY");
    const nextBillingDate = isTrial ? trialExpirationDate : isAnnual
        ? moment().add(1, "year").format("MMMM Do, YYYY")
        : moment().add(1, "month").format("MMMM Do, YYYY");

    const taxString = taxAmount ? t(" plus tax") : "";

    if (expandable) {
        return (
            <FlexLayout direction="column" gap={10} width="fill">
                {!isSummaryExpanded ? (
                    <FlexLayout direction="column" gap={5} padding="0 10px">
                        <FlexLayout justify="space-between" align="center">
                            <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueToday) })}</DueTodayText>
                            <SummaryToggle onClick={() => setIsSummaryExpanded(true)}>
                                {t("VIEW SUMMARY")}
                                <DownArrowIcon />
                            </SummaryToggle>
                        </FlexLayout>
                        <FlexLayout justify="space-between" align="center">
                            <DueDateText>{t("Due {{date}}: {{amount}}", { date: nextBillingDate, amount: formatPrice(currentPromoAppliedPrice) })}</DueDateText>
                        </FlexLayout>
                    </FlexLayout>
                ) : (
                    <FlexLayout direction="column" gap={5} padding="0 10px">
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("{{productDisplayName}} - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                            <TextContent helper>{formatPrice(currentPromoAppliedPrice)}/{t(billingInterval)}</TextContent>
                        </FlexLayout>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("{{price}} x {{count}} {{unit}}", { price: formatPrice(isAnnual ? (currentPromoAppliedPrice / 12) : currentPromoAppliedPrice), count: isAnnual ? 12 : 1, unit: t(isAnnual ? "months" : "month") })}</TextContent>
                            <TextContent helper>{formatPrice(currentPromoAppliedPrice)}</TextContent>
                        </FlexLayout>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("Tax")}</TextContent>
                            <TextContent helper>—</TextContent>
                        </FlexLayout>
                        <Divider margin={10} />
                        <FlexLayout justify="space-between">
                            <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueToday) })}</DueTodayText>
                            <SummaryToggle onClick={() => setIsSummaryExpanded(false)}>
                                {t("HIDE SUMMARY")}
                                <UpArrowIcon />
                            </SummaryToggle>
                        </FlexLayout>
                        <FlexLayout justify="space-between" align="center">
                            <DueDateText>{t("Due {{date}}", { date: nextBillingDate })}</DueDateText>
                        </FlexLayout>
                    </FlexLayout>
                )}
            </FlexLayout>
        );
    }

    return (
        <SummaryCard>
            <SummaryTitle>{t("Summary")}</SummaryTitle>
            <SummaryContent>
                <FlexLayout width="fill" direction="column" gap={5}>
                    <FlexLayout justify="space-between">
                        <TextContent helper>{t("{{productDisplayName}} - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                        <TextContent helper>{formatPrice(currentPromoAppliedPrice)} / {t(billingInterval)}</TextContent>
                    </FlexLayout>
                </FlexLayout>
                <Divider />
                <FlexLayout width="fill" direction="column" gap={5}>
                    <FlexLayout justify="space-between">
                        <TextContent helper>
                            {isAnnual
                                ? t("{{price}} x {{count}} months", { price: formatPrice(annualCostAtAnnualRate / 12), count: 12 })
                                : t("{{price}} x {{count}} month", { price: formatPrice(annualCostAtMonthlyRate / 12), count: 1 })}
                        </TextContent>
                        <TextContent helper><span data-testid="plan-summary-subtotal">{formatPrice(currentPromoAppliedPrice)}</span></TextContent>
                    </FlexLayout>
                    <FlexLayout justify="space-between">
                        <TextContent helper>{t("Tax")}</TextContent>
                        <TextContent helper>{taxAmount ? t("+ {{amount}}", { amount: formatPrice(taxAmount) }) : t("-")}</TextContent>
                    </FlexLayout>
                </FlexLayout>
                <Divider />
                <FlexLayout width="fill" direction="column" gap={5}>
                    <FlexLayout justify="space-between">
                        <TextContent sectionLabel>{t("Due Today")}</TextContent>
                        <TextContent sectionLabel>{formatPrice(dueToday)}{dueToday > 0 ? taxString : ""}</TextContent>
                    </FlexLayout>
                    <FlexLayout justify="space-between">
                        <TextContent helper>{t("Due {{date}}", { date: nextBillingDate })}</TextContent>
                        <TextContent helper>{formatPrice(dueNextBillingCycle)}{dueNextBillingCycle > 0 ? taxString : ""}</TextContent>
                    </FlexLayout>
                </FlexLayout>
                <FlexLayout width="fill" justify="center">
                    {!promo ? <PromoForm variant="B" verifyPromoCode={handleVerifyPromoCode} initialPromotionCode={initialPromotionCode} /> : <PromoCoupon variant="B" coupon={promo.coupon} />}
                </FlexLayout>
            </SummaryContent>
        </SummaryCard>
    );
}
